import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SweepError } from '@server/sweep-errors';
import { StageType } from '../../../../types/enums/StageType';

import {
  dropTransientNewFunnelInPlace,
  removeAllTransientFunnelData,
  setSfNewFunnelTransientDataStage,
  removeNewFunnelTransientData,
  selectPlacingTemplate,
  setIsLoadingCursor,
  selectTransientCreateSfFunnel,
  selectTransientImportSfFunnel,
  selectTransientCreateThirdPartyFunnel,
  selectTransientImportThirdPartyFunnel,
  selectTransientFunnel,
  selectTransientImportMetadataElement,
} from '../../../../reducers/canvasPageStateReducer';
import {
  CanvasElementType,
  CanvasEvent,
  CanvasMoveGroupEvent,
  CanvasMoveNodeEvent,
  CanvasEventType,
  CanvasAddNodeEvent,
  CanvasMoveStandaloneNodeEvent,
  CanvasEscapeFromTempNodeEvent,
  CanvasRemoveGroupEvent,
  CanvasSwapNodesEvent,
  CanvasAddSectionEvent,
  CanvasResizeSectionEvent,
  CanvasMoveSectionEvent,
  CanvasChangeSectionEvent,
  CanvasMoveStickyNoteEvent,
  CanvasResizeStickyNoteEvent,
  CanvasChangeStickyNoteEvent,
  CanvasRemoveSectionEvent,
  CanvasUngroupSectionEvent,
  CanvasAddStickyNoteEvent,
  CanvasRemoveStickyNoteEvent,
  CanvasMoveTextNodeEvent,
  CanvasResizeTextNodeEvent,
  CanvasAddTextNodeEvent,
  CanvasRemoveTextNodeEvent,
  CanvasChangeTextNodeDataEvent,
} from '../../../sweep-canvas/canvas-types';
import {
  addSfFunnel,
  addMultipleSfFunnels,
  addSfStage,
  addThirdPartyFunnelToCanvas,
  moveSfStages,
  removeFunnel,
  moveSfFunnels,
  addThirdPartyFunnelStageToCanvas,
  moveThirdPartyFunnelStagesOnCanvas,
  moveThirdPartyFunnelsOnCanvas,
  reorderSfSteps,
  reorderThirdPartySteps,
  addMetadataElementsToCanvas as addMetadataElementsToCanvasAction,
  moveMetadataElement,
  addSectionToCanvas,
  updateSectionData,
  resizeSectionOnCanvas,
  moveSectionOnCanvas,
  addStickyNoteToCanvas,
  moveStickyNoteOnCanvas,
  resizeStickyNoteOnCanvas,
  updateStickyNoteData,
  removeSectionFromCanvas,
  removeStickyNoteFromCanvas,
  ungroupSectionFromCanvas,
  bulkMoveElementsOnCanvas,
  moveTextNodeOnCanvas,
  resizeTextNodeOnCanvas,
  removeTextNodeFromCanvas,
  addTextNode,
  changeTextNodeData,
} from '../../../../reducers/united-canvas/unitedCanvasReducer';
import { aStage } from '../../../../models/stageModel';
import { DEFAULT_LAYERS_ID, enableLayerFor } from '../../../../reducers/canvasLayersReducer';
import { VisibilityLayers } from '../../../../types/enums/VisibilityLayers';
import useEventListener from '../../../common/useEventListener';
import cloneDeep from 'lodash/cloneDeep';
import noop from 'lodash/noop';
import { useSweepNotifications } from '../../../notifications/useSweepNotifications';
import { SweepNotificationVariant } from '../../../../reducers/notificationsReducer';
import { CanvasNodesMouseMove } from '../../../sweep-canvas/useCanvasNodesMouseMove';
import { useTemplatesFacade } from '../../../../apis/facades/useTemplatesFacade';
import { selectFieldsPerTemplateMapping } from '../../funnel-map-flow/templates/templates-tab/templateFieldsMappingReducer';
import { TemplateFieldMapping } from '../../funnel-map-flow/templates/templatesTypes';
import { useSfAddFunnelToTheCanvas } from '../../funnel-map-flow/add-funnel/useSfAddFunnelToTheCanvas';
import { telemetry } from '../../../../telemetry';
import { ACTIONS_EVENTS } from '../../../../services/events';
import useSendBiEvent from '../../../../hooks/useSendBiEvent';
import { TRANSIENT_GROUP_ID } from '../../../funnel-map-canvas/useFunnelMapElements';
import { getFunnelTypeFromNodeId } from '../../../funnel-map-canvas/helper';
import { FunnelType } from '../../../../types/enums/FunnelType';
import groupBy from 'lodash/groupBy';
import { addThirdPartyFunnel } from '../../../third-party-funnels/thirdPartyFunnelsReducer';
import { useFunnelMapCanvasCtx } from '../../../funnel-map-canvas/FunnelMapCanvasCtx';
import { useThirdPartyFunnelsApi } from '../../../third-party-funnels/useThirdPartyFunnelsApi';
import { FunnelMapModel } from '../../../../models/funnelMapModel';
import { getFullMetadataElementId } from '../../funnel-map-flow/metadata-elements/metadataElementUtils';

const getFunnelIdBySweepStage = (funnelsData: FunnelsData): { [stageId: string]: string } =>
  Object.keys(funnelsData)
    .map((funnelId) =>
      funnelsData[funnelId].funnelDetails.stages.map((stage) => ({
        funnelId,
        stageId: stage._stageId,
      })),
    )
    .flat()
    .reduce(
      (
        prev: {
          [stageId: string]: string;
        },
        curr,
      ) => {
        prev[curr.stageId] = curr.funnelId;
        return prev;
      },
      {},
    );

const constructSwapMessage = (sourceStageName: string, targetStageName: string) => {
  return `Swapped '${sourceStageName}' and '${targetStageName}' steps. ${navigator.platform.includes('Mac') ? 'Command+Z' : 'Ctrl+Z'} to revert.`;
};

export const useUnitedConnectedFunnelMapCanvas = ({
  funnelMap,
  crmOrgId,
  readonly,
  layersId = DEFAULT_LAYERS_ID,
}: {
  funnelMap: FunnelMap;
  crmOrgId: string;
  readonly?: boolean;

  layersId?: string;
}) => {
  const transientFunnel = useSelector(selectTransientFunnel);

  const dispatch = useDispatch();
  const sendBiEvent = useSendBiEvent();
  const { addNotification } = useSweepNotifications();

  const { onCreateSfFunnel } = useSfAddFunnelToTheCanvas(crmOrgId);
  const { createThirdPartyFunnel } = useThirdPartyFunnelsApi();

  const { setEditingThirdPartyStepId } = useFunnelMapCanvasCtx();
  const transientSfFunnelData = useSelector(selectTransientImportSfFunnel);
  const importedSfFunnel = transientSfFunnelData?.data.funnel;
  const importThirdPartyFunnel = useSelector(selectTransientImportThirdPartyFunnel)?.data
    .thirdPartyFunnel;

  const importedMetadataElements = useSelector(selectTransientImportMetadataElement)?.data;
  const placingTemplate = useSelector(selectPlacingTemplate);

  const { funnelsData } = funnelMap;
  const _funnelMapWithTransientFunnel = cloneDeep(funnelMap);
  const _funnelsDataWithTransientFunnel = cloneDeep(funnelsData);

  if (importedSfFunnel) {
    _funnelMapWithTransientFunnel.funnels[importedSfFunnel.id] = {
      position: {
        column: 0,
        row: 0,
      },
    };
    _funnelsDataWithTransientFunnel[importedSfFunnel.id] = importedSfFunnel;
  }
  if (placingTemplate) {
    placingTemplate.template.funnelTemplates.forEach((template) => {
      _funnelMapWithTransientFunnel.funnels[template.id] = {
        position: template.position,
      };
      _funnelsDataWithTransientFunnel[template.id] = {
        id: template.id,
        name: template.name,
        recordType: {
          description: '',
          label: '',
          name: template.objectName,
          objectName: template.objectName,
        },
        funnelDetails: template.templateFunnelDetails,
        description: '',
        accountId: '',
        createdAt: '',
        createdById: '',
        updatedAt: '',
        snapshotsIds: [],
        updatedById: '',
        apiName: '', // not set yet
        crmOrgId: placingTemplate.crmOrgId,
      };
    });
  }
  const funnelIdBySweepStage = getFunnelIdBySweepStage(_funnelsDataWithTransientFunnel);

  const funnelDetailsStages = Object.keys(funnelsData)
    .map((funnelId) => funnelsData[funnelId].funnelDetails.stages)
    .flat();

  const onSweepElementsChange = useCallback(
    (event: CanvasEvent) => {
      const getGroupTypeFromNodeId = (parentId: string) => {
        const groupType =
          parentId === TRANSIENT_GROUP_ID
            ? transientFunnel?.type
            : getFunnelTypeFromNodeId(funnelMap, parentId);
        return groupType;
      };

      const onAddThirdPartyNode = async (event: CanvasAddNodeEvent) => {
        const { newNode, edgeTransformations, newEdge, nodesToMove } = event;

        if (!newNode.parentId) {
          throw new Error('Error. There is no parentId on the node');
        }

        const newStep: ThirdPartyStep = {
          id: newNode.id,
          name: newNode.name,
          stepConnections: [],
          position: newNode.position,
          funnelLinks: [],
        };
        dispatch(
          addThirdPartyFunnelStageToCanvas({
            newStep,
            funnelId: newNode.parentId,
            stepConnectionChanges: Object.values(edgeTransformations).map((edge) => ({
              id: edge.id,
              originId: edge.source,
              targetId: edge.target,
            })),
            stepPositionChanges: nodesToMove.map((nodeMove) => ({
              stepId: nodeMove.nodeId,
              newPosition: nodeMove.newPosition,
            })),
            newStepConnection: newEdge
              ? { id: newEdge.id, originId: newEdge.source, targetId: newEdge.target }
              : undefined,
          }),
        );
      };
      const onAddNode = async (event: CanvasAddNodeEvent) => {
        const { newNode } = event;
        if (!newNode.parentId) {
          throw new Error('Error. There is no parentId on the node');
        }

        const groupType = getGroupTypeFromNodeId(newNode.parentId);

        switch (groupType) {
          case FunnelType.SALESFORCE:
            onAddSfNode(event);
            break;
          case FunnelType.THIRD_PARTY:
            onAddThirdPartyNode(event);
            break;
          default:
            break;
        }
      };

      const onAddSfNode = async (event: CanvasAddNodeEvent) => {
        const { newNode, newGroup } = event;
        let funnelId = newNode.parentId;

        if (!funnelId) {
          throw new Error('Error. There is no parentID on the node');
        }

        const isNurturingBucket = newNode.type === CanvasElementType.NURTURING_BUCKET;
        const stageType: StageType = isNurturingBucket ? StageType.NURTURING : StageType.REGULAR;

        const newStage = aStage({
          _stageId: newNode.id,
          _branchIndex: newNode.position.row,
          _stageColumnIndex: newNode.position.column,
          stageName: newNode.name.trim(),
          stageDescription: newNode.name.trim(),
          stageType,
        });

        const stagesPositionChanges = event.nodesToMove.map((nodeMove) => ({
          stageId: nodeMove.nodeId,
          newPosition: nodeMove.newPosition,
        }));

        const stagesNextStageChanges = Object.keys(event.edgeTransformations).map((edgeId) => ({
          stageId: event.edgeTransformations[edgeId].source,
          exitCriteriaId: event.edgeTransformations[edgeId].id,
          newNextStageId: event.edgeTransformations[edgeId].target,
        }));
        const newExitCriteria = event.newEdge
          ? {
              stageId: event.newEdge?.source,
              nextStageId: event.newEdge?.target,
            }
          : undefined;
        const newFunnel = newGroup?.metadata
          ? {
              id: newGroup.id,
              leadingObject: newGroup.metadata,
              name: newGroup.name,
              position: newGroup.position,
              crmOrgId,
            }
          : undefined;
        if (
          funnelId === TRANSIENT_GROUP_ID &&
          transientFunnel?.type === FunnelType.SALESFORCE &&
          transientFunnel?.importType === 'create' &&
          crmOrgId
        ) {
          dispatch(
            setSfNewFunnelTransientDataStage({
              stage: newStage,
            }),
          );
          try {
            const funnel = await onCreateSfFunnel({
              funnelDetails: {
                stages: [newStage],
                leadingObject: transientFunnel.data.leadingObject,
                plugins: {},
              },
              funnelName: transientFunnel.name,
              position: event.parentCanvasNodePosition || { column: 0, row: 0 },
              sectionId: transientFunnel.sectionId,
            });

            funnelId = funnel.id;
          } catch (err) {
            telemetry.captureError(err);
            addNotification({
              message: 'Error creating funnel view',
              variant: SweepNotificationVariant.Error,
            });
            dispatch(removeNewFunnelTransientData());
          }
        } else {
          dispatch(
            addSfStage({
              funnelId,
              stagesPositionChanges,
              newStage,
              stagesNextStageChanges,
              newExitCriteria,
              newFunnel,
            }),
          );
        }

        if (isNurturingBucket) {
          dispatch(enableLayerFor({ layersId, layer: VisibilityLayers.NURTURING_STEPS }));
        }
      };

      const onMoveSfNodes = (nodeChanges: CanvasMoveNodeEvent[]) => {
        let funnelId: string | undefined = undefined;
        const stagesPositionChanges = nodeChanges.map((nodeMove) => {
          if (nodeMove.parentId) {
            funnelId = nodeMove.parentId;
          }
          funnelId = nodeMove.parentId;
          return {
            stageId: nodeMove.nodeId,
            newPosition: nodeMove.newPosition,
          };
        });
        if (!funnelId) {
          throw new Error('Error. There is no parentID on the node');
        }
        dispatch(moveSfStages({ funnelId, stagesPositionChanges }));
      };

      const onMoveThirdPartyNodes = (nodeMoveChanges: CanvasMoveNodeEvent[]) => {
        const stepPositionChanges = nodeMoveChanges
          .filter(({ parentId }) => parentId)
          .map((nodeMove) => ({
            funnelId: nodeMove.parentId as string,
            stepId: nodeMove.nodeId,
            newPosition: nodeMove.newPosition,
          }));
        dispatch(moveThirdPartyFunnelStagesOnCanvas({ stepPositionChanges }));
      };

      const onMoveNodes = (nodeMoveChanges: CanvasMoveNodeEvent[]) => {
        if (!nodeMoveChanges.length) {
          return;
        }
        const changesByGroupType = groupBy(nodeMoveChanges, (nodeChange) =>
          getGroupTypeFromNodeId(nodeChange.parentId ?? nodeChange.nodeId),
        );

        Object.entries(changesByGroupType).forEach(([groupType, events]) => {
          switch (groupType) {
            case FunnelType.SALESFORCE:
              onMoveSfNodes(events);
              break;
            case FunnelType.THIRD_PARTY:
              onMoveThirdPartyNodes(events);
              break;
            default:
              break;
          }
        });
      };

      const onMoveMetadataElement = (event: CanvasMoveStandaloneNodeEvent) => {
        const { nodeId, newPosition, parentId } = event;
        dispatch(moveMetadataElement({ fullMetadataId: nodeId, newPosition, sectionId: parentId }));
      };

      const onMoveSfFunnels = (events: CanvasMoveGroupEvent[]) => {
        const groupChanges = events.map(({ nodeId, newPosition, parentId }) => ({
          funnelId: nodeId,
          newPosition,
          sectionId: parentId,
        }));

        dispatch(moveSfFunnels(groupChanges));
      };

      const onMoveThirdPartyFunnel = (events: CanvasMoveGroupEvent[]) => {
        const groupChanges = events.map(({ nodeId, newPosition, parentId }) => ({
          funnelId: nodeId,
          newPosition,
          sectionId: parentId,
        }));
        dispatch(moveThirdPartyFunnelsOnCanvas(groupChanges));
      };

      const onMoveGroup = (events: CanvasMoveGroupEvent[]) => {
        const groupsByType = groupBy(events, (event) => getGroupTypeFromNodeId(event.nodeId));

        Object.entries(groupsByType).forEach(([groupType, groupsToMove]) => {
          switch (groupType) {
            case FunnelType.SALESFORCE:
              onMoveSfFunnels(groupsToMove);
              break;
            case FunnelType.THIRD_PARTY:
              onMoveThirdPartyFunnel(groupsToMove);
              break;
            default:
              break;
          }
        });
      };

      const onEscapeFromShadowStep = (event: CanvasEscapeFromTempNodeEvent) => {
        const type = getGroupTypeFromNodeId(event.parentId);
        if (type === FunnelType.SALESFORCE) {
          const isGroupEmpty = !Boolean(
            funnelDetailsStages.find(
              (stage) => funnelIdBySweepStage[stage._stageId] === event.parentId,
            ),
          );
          if (isGroupEmpty) {
            dispatch(removeFunnel({ funnelId: event.parentId }));
          }
        }
        if (type === FunnelType.THIRD_PARTY) {
          setEditingThirdPartyStepId(undefined);
        }
      };

      const onRemoveGroup = (event: CanvasRemoveGroupEvent) => {
        dispatch(removeFunnel({ funnelId: event.groupId }));
      };

      const onSwapNodes = (event: CanvasSwapNodesEvent) => {
        const fmModel = new FunnelMapModel(funnelMap);

        const { sourceNode, targetNode } = event;
        if (
          !sourceNode.parentId ||
          !targetNode.parentId ||
          sourceNode.parentId !== targetNode.parentId
        ) {
          return;
        }
        const sourceNodeGroupType = getGroupTypeFromNodeId(sourceNode.parentId);

        switch (sourceNodeGroupType) {
          case FunnelType.SALESFORCE: {
            dispatch(
              reorderSfSteps({
                groupId: sourceNode.parentId,
                switchStepPairs: [[sourceNode.id, targetNode.id]],
              }),
            );
            const sourceStageName = fmModel
              .funnelDetailsModelById(sourceNode.parentId)
              .stageById(sourceNode.id)
              .getStageName();
            const targetStageName = fmModel
              .funnelDetailsModelById(targetNode.parentId)
              .stageById(targetNode.id)
              .getStageName();

            addNotification({
              message: constructSwapMessage(sourceStageName, targetStageName),
              variant: SweepNotificationVariant.Success,
            });
            break;
          }
          case FunnelType.THIRD_PARTY:
            dispatch(
              reorderThirdPartySteps({
                groupId: sourceNode.parentId,
                switchStepPairs: [[sourceNode.id, targetNode.id]],
              }),
            );
            const sourceStepName =
              funnelMap.thirdPartyFunnelsData[sourceNode.parentId].steps.find(
                (step) => step.id === sourceNode.id,
              )?.name || '';
            const targetStepName =
              funnelMap.thirdPartyFunnelsData[targetNode.parentId].steps.find(
                (step) => step.id === targetNode.id,
              )?.name || '';

            addNotification({
              message: constructSwapMessage(sourceStepName, targetStepName),
              variant: SweepNotificationVariant.Success,
            });
            break;
          default:
            break;
        }
      };

      const onAddSection = (event: CanvasAddSectionEvent) => {
        const nodesToMove = event.nodesToMove?.map((node) => ({
          id: node.id,
          position: node.position,
          xyPosition: node.xyPosition,
          type: getGroupTypeFromNodeId(node.id) as FunnelType,
        }));
        dispatch(
          addSectionToCanvas({
            sectionId: event.id,
            xyPosition: event.xyPosition,
            width: event.width,
            height: event.height,
            color: event.color,
            name: event.name,
            nodesToMove,
          }),
        );
      };

      const onMoveSection = (event: CanvasMoveSectionEvent) => {
        dispatch(moveSectionOnCanvas({ sectionId: event.id, newPosition: event.xyPosition }));
      };

      const onResizeSection = (event: CanvasResizeSectionEvent) => {
        dispatch(
          resizeSectionOnCanvas({
            sectionId: event.id,
            width: event.width,
            height: event.height,
            xyPosition: event.xyPosition,
          }),
        );
      };

      const onSectionChangeData = (event: CanvasChangeSectionEvent) => {
        dispatch(updateSectionData({ sectionId: event.id, color: event.color, name: event.name }));
      };

      const onMoveStickyNote = (event: CanvasMoveStickyNoteEvent) => {
        dispatch(
          moveStickyNoteOnCanvas({
            stickyNoteId: event.id,
            newPosition: event.xyPosition,
            sectionId: event.parentId,
          }),
        );
      };

      const onResizeStickyNote = (event: CanvasResizeStickyNoteEvent) => {
        dispatch(
          resizeStickyNoteOnCanvas({
            stickyNoteId: event.id,
            width: event.width,
            height: event.height,
            xyPosition: event.xyPosition,
            textScale: event.textScale,
          }),
        );
      };

      const onChangeStickyNoteData = (event: CanvasChangeStickyNoteEvent) => {
        dispatch(
          updateStickyNoteData({
            stickyNoteId: event.id,
            color: event.color,
            richText: event.richText,
            textScale: event.textScale,
            height: event.height,
          }),
        );
      };

      const onAddStickyNote = (event: CanvasAddStickyNoteEvent) => {
        const { id, parentId, ...stickyNote } = event;
        dispatch(
          addStickyNoteToCanvas({
            stickyNote: {
              stickyNoteId: id,
              sectionId: parentId,
              ...stickyNote,
            },
          }),
        );
      };

      const onAddTextNode = (event: CanvasAddTextNodeEvent) => {
        const { id, parentId, ...textNode } = event;
        dispatch(
          addTextNode({
            textNode: {
              textId: id,
              sectionId: parentId,
              ...textNode,
            },
          }),
        );
      };

      const onChangeTextNodeData = (event: CanvasChangeTextNodeDataEvent) => {
        dispatch(
          changeTextNodeData({
            textNodeId: event.id,
            richText: event.richText,
            textScale: event.textScale,
            height: event.height,
          }),
        );
      };

      const onMoveTextNode = (event: CanvasMoveTextNodeEvent) => {
        dispatch(
          moveTextNodeOnCanvas({
            textNodeId: event.id,
            newPosition: event.xyPosition,
            sectionId: event.parentId,
          }),
        );
      };

      const onResizeTextNode = (event: CanvasResizeTextNodeEvent) => {
        dispatch(
          resizeTextNodeOnCanvas({
            textNodeId: event.id,
            width: event.width,
            height: event.height,
            xyPosition: event.xyPosition,
            textScale: event.textScale,
          }),
        );
      };

      const onAddOrChangeTextNodeData = (event: CanvasAddTextNodeEvent) => {
        dispatch(
          addTextNode({
            textNode: {
              textId: event.id,
              sectionId: event.parentId,
              richText: event.richText,
              xyPosition: event.xyPosition,
              width: event.width,
              height: event.height,
              textScale: event.textScale,
            },
          }),
        );
      };

      const onRemoveTextNode = (event: CanvasRemoveTextNodeEvent) => {
        dispatch(removeTextNodeFromCanvas({ textNodeId: event.id }));
      };

      const onRemoveSection = (event: CanvasRemoveSectionEvent) => {
        dispatch(removeSectionFromCanvas({ sectionId: event.id }));
      };

      const onUngroupSection = (event: CanvasUngroupSectionEvent) => {
        // Map nodesToMove with correct FunnelType
        const nodesToMove = event.nodesToMove.map((node) => ({
          ...node,
          type: getFunnelTypeFromNodeId(funnelMap, node.nodeId) || node.type,
        }));

        dispatch(
          ungroupSectionFromCanvas({
            sectionId: event.id,
            nodesToMove,
          }),
        );
      };

      const onRemoveStickyNote = (event: CanvasRemoveStickyNoteEvent) => {
        dispatch(removeStickyNoteFromCanvas({ stickyNoteId: event.id }));
      };
      // Handle the event based on its type
      switch (event.type) {
        case CanvasEventType.MoveNode:
          onMoveNodes([event]);
          break;
        case CanvasEventType.MoveGroup:
          onMoveGroup([event]);
          break;
        case CanvasEventType.MoveMultipleNodes:
          // Group and transform all move events for bulk dispatch
          const moveNodeEvents = event.moveEvents.filter(
            (e) => e.type === CanvasEventType.MoveNode,
          ) as CanvasMoveNodeEvent[];
          const moveGroupEvents = event.moveEvents.filter(
            (e) => e.type === CanvasEventType.MoveGroup,
          ) as CanvasMoveGroupEvent[];
          const moveStandaloneEvents = event.moveEvents.filter(
            (e) => e.type === CanvasEventType.MoveStandaloneNode,
          ) as CanvasMoveStandaloneNodeEvent[];
          const moveSectionEvents = event.moveEvents.filter(
            (e) => e.type === CanvasEventType.MoveSection,
          ) as CanvasMoveSectionEvent[];
          const moveStickyNoteEvents = event.moveEvents.filter(
            (e) => e.type === CanvasEventType.MoveStickyNote,
          ) as CanvasMoveStickyNoteEvent[];
          const moveTextNodeEvents = event.moveEvents.filter(
            (e) => e.type === CanvasEventType.MoveTextNode,
          ) as CanvasMoveTextNodeEvent[];
          // Group nodes by funnel type
          const nodesByFunnelType = groupBy(moveNodeEvents, (nodeChange) =>
            getGroupTypeFromNodeId(nodeChange.parentId ?? nodeChange.nodeId),
          );

          // Group SF stages by funnel
          const sfStages = nodesByFunnelType[FunnelType.SALESFORCE]
            ? Object.entries(
                groupBy(nodesByFunnelType[FunnelType.SALESFORCE], (node) => node.parentId),
              ).map(([funnelId, nodes]) => ({
                funnelId,
                stagesPositionChanges: nodes.map((node) => ({
                  stageId: node.nodeId,
                  newPosition: node.newPosition,
                })),
              }))
            : undefined;

          // Third-party stages
          const thirdPartyStages = nodesByFunnelType[FunnelType.THIRD_PARTY]
            ?.filter(({ parentId }) => parentId)
            .map((node) => ({
              funnelId: node.parentId as string,
              stepId: node.nodeId,
              newPosition: node.newPosition,
            }));

          // Group funnels by type
          const groupsByType = groupBy(moveGroupEvents, (event) =>
            getGroupTypeFromNodeId(event.nodeId),
          );

          // SF funnels
          const sfFunnels = groupsByType[FunnelType.SALESFORCE]?.map(
            ({ nodeId, newPosition, parentId }) => ({
              funnelId: nodeId,
              newPosition,
              sectionId: parentId,
            }),
          );

          // Third-party funnels
          const thirdPartyFunnels = groupsByType[FunnelType.THIRD_PARTY]?.map(
            ({ nodeId, newPosition, parentId }) => ({
              funnelId: nodeId,
              newPosition,
              sectionId: parentId,
            }),
          );

          // Metadata elements
          const metadataElements = moveStandaloneEvents.map(
            ({ nodeId, newPosition, parentId }) => ({
              fullMetadataId: nodeId,
              newPosition,
              sectionId: parentId,
            }),
          );

          // Sections
          const sections = moveSectionEvents.map(({ id, xyPosition }) => ({
            sectionId: id,
            newPosition: xyPosition,
          }));

          // Sticky notes
          const stickyNotes = moveStickyNoteEvents.map(({ id, xyPosition, parentId }) => ({
            stickyNoteId: id,
            newPosition: xyPosition,
            sectionId: parentId,
          }));

          const textNodes = moveTextNodeEvents.map(({ id, xyPosition, parentId }) => ({
            textNodeId: id,
            newPosition: xyPosition,
            sectionId: parentId,
          }));

          // Dispatch single bulk action
          dispatch(
            bulkMoveElementsOnCanvas({
              sfStages,
              sfFunnels,
              thirdPartyStages,
              thirdPartyFunnels,
              metadataElements: metadataElements.length > 0 ? metadataElements : undefined,
              sections: sections.length > 0 ? sections : undefined,
              stickyNotes: stickyNotes.length > 0 ? stickyNotes : undefined,
              textNodes: textNodes.length > 0 ? textNodes : undefined,
            }),
          );
          break;
        case CanvasEventType.AddNode:
          onAddNode(event);
          break;
        case CanvasEventType.EscapeFromShadowStep:
          onEscapeFromShadowStep(event);
          break;
        case CanvasEventType.RemoveGroup:
          onRemoveGroup(event);
          break;
        case CanvasEventType.SwapNodes:
          onSwapNodes(event);
          break;
        case CanvasEventType.MoveStandaloneNode:
          onMoveMetadataElement(event);
          break;
        case CanvasEventType.AddSection:
          onAddSection(event);
          break;
        case CanvasEventType.MoveSection:
          onMoveSection(event);
          break;
        case CanvasEventType.ResizeSection:
          onResizeSection(event);
          break;
        case CanvasEventType.SectionChangeData:
          onSectionChangeData(event);
          break;
        case CanvasEventType.MoveStickyNote:
          onMoveStickyNote(event);
          break;
        case CanvasEventType.ResizeStickyNote:
          onResizeStickyNote(event);
          break;
        case CanvasEventType.ChangeStickyNoteData:
          onChangeStickyNoteData(event);
          break;
        case CanvasEventType.RemoveSection:
          onRemoveSection(event);
          break;
        case CanvasEventType.UngroupSection:
          onUngroupSection(event);
          break;
        case CanvasEventType.AddStickyNote:
          onAddStickyNote(event);
          break;
        case CanvasEventType.RemoveStickyNote:
          onRemoveStickyNote(event);
          break;
        case CanvasEventType.AddTextNode:
          onAddOrChangeTextNodeData(event);
          break;
        case CanvasEventType.MoveTextNode:
          onMoveTextNode(event);
          break;
        case CanvasEventType.ResizeTextNode:
          onResizeTextNode(event);
          break;
        case CanvasEventType.AddTextNode:
          onAddTextNode(event);
          break;
        case CanvasEventType.RemoveTextNode:
          onRemoveTextNode(event);
          break;
        case CanvasEventType.ChangeTextNodeData:
          onChangeTextNodeData(event);
          break;
        default:
          break;
      }
    },
    [
      transientFunnel,
      funnelMap,
      dispatch,
      crmOrgId,
      onCreateSfFunnel,
      addNotification,
      layersId,
      funnelDetailsStages,
      funnelIdBySweepStage,
      setEditingThirdPartyStepId,
    ],
  );

  const createTransientSfFunnel = useSelector(selectTransientCreateSfFunnel);
  const createTransientThirdPartyFunnel = useSelector(selectTransientCreateThirdPartyFunnel);

  const { post_templateConvert } = useTemplatesFacade();

  const fieldsPerTemplateMapping = useSelector(
    selectFieldsPerTemplateMapping(crmOrgId, placingTemplate?.template.id),
  );

  useEventListener('keydown', (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      dispatch(removeAllTransientFunnelData());
    }
  });

  const _moveGroups: CanvasNodesMouseMove | undefined = useMemo(() => {
    if (readonly) {
      return undefined;
    }

    // Handles the case when a template is being imported
    if (placingTemplate && crmOrgId) {
      const groupIds = placingTemplate.template.funnelTemplates.map((template) => template.id);

      const fieldMappings: TemplateFieldMapping[] = fieldsPerTemplateMapping?.enabled
        ? Object.entries(fieldsPerTemplateMapping.sweepFieldsByApiName).map(
            ([apiName, { fieldIds }]) => ({
              fromTemplateFieldId: apiName,
              toSweepFieldId: fieldIds[fieldIds.length - 1],
            }),
          )
        : [];

      return {
        nodeIds: groupIds,
        onDrop: async (nodeMoveEvent) => {
          dispatch(setIsLoadingCursor(true));
          dispatch(removeAllTransientFunnelData());
          try {
            const { funnelsData, funnels } = await post_templateConvert({
              crmOrgId,
              templateId: placingTemplate.template.id,
              fieldMapping: placingTemplate.useFieldMapping ? fieldMappings : [],
            });

            const funnelsWithPositions = Object.entries(funnels).map(([funnelId, { position }]) => {
              return {
                funnel: funnelsData[funnelId],
                position: {
                  column: nodeMoveEvent.canvasGridPosition.column + position.column,
                  row: nodeMoveEvent.canvasGridPosition.row + position.row,
                },
                sectionId: nodeMoveEvent.parentId,
              };
            });
            sendBiEvent({
              name: ACTIONS_EVENTS.funnelsAddFunnel,
              props: {
                object: Object.values(funnelsData)
                  .map((f) => f.funnelDetails.leadingObject.objectName)
                  .join(','),
              },
            });
            dispatch(addMultipleSfFunnels(funnelsWithPositions));
            dispatch(setIsLoadingCursor(false));
          } catch (err: any) {
            let message = 'Error importing template';

            if (err?.response?.data?.sweepError === SweepError.NoFieldPermission) {
              message = err?.response?.data?.message;
            } else {
              telemetry.captureError(err);
            }

            addNotification({
              message,
              variant: SweepNotificationVariant.Error,
            });
          } finally {
            dispatch(setIsLoadingCursor(false));
          }
        },
      };
    }

    // Handles the case when a funnel is being imported
    const importingFunnelId = importedSfFunnel?.id;
    if (importingFunnelId) {
      return {
        nodeIds: [importingFunnelId],
        onDrop: (nodeMoveEvent) => {
          dispatch(removeAllTransientFunnelData());
          sendBiEvent({
            name: ACTIONS_EVENTS.funnelsAddFunnel,
            props: { object: importedSfFunnel.funnelDetails.leadingObject.objectName },
          });
          dispatch(
            addSfFunnel({
              funnel: importedSfFunnel,
              position: nodeMoveEvent.canvasGridPosition,
              sectionId: nodeMoveEvent.parentId,
            }),
          );
        },
      };
    }

    if (importThirdPartyFunnel) {
      return {
        nodeIds: [importThirdPartyFunnel.id],
        onDrop: (nodeMoveEvent) => {
          dispatch(removeAllTransientFunnelData());
          dispatch(
            addThirdPartyFunnelToCanvas({
              thirdPartyFunnel: importThirdPartyFunnel,
              position: nodeMoveEvent.canvasGridPosition,
              sectionId: nodeMoveEvent.parentId,
            }),
          );
        },
      };
    }

    if (importedMetadataElements) {
      return {
        nodeIds: importedMetadataElements.metadataElements.map(getFullMetadataElementId),
        onDrop: (nodeMoveEvent) => {
          dispatch(removeAllTransientFunnelData());
          const metadataElements = importedMetadataElements.metadataElements.map(
            (metadataElement) => ({
              ...metadataElement,
              position: {
                column: nodeMoveEvent.canvasGridPosition.column + metadataElement.position.column,
                row: nodeMoveEvent.canvasGridPosition.row + metadataElement.position.row,
              },
            }),
          );
          dispatch(
            addMetadataElementsToCanvasAction({
              metadataElements,
              connections: importedMetadataElements.connections,
              sectionId: nodeMoveEvent.parentId,
            }),
          );
        },
      };
    }

    // Handles the case when a new funnel is being created
    if (createTransientSfFunnel && createTransientSfFunnel.isPlacingFunnel) {
      return {
        nodeIds: [TRANSIENT_GROUP_ID],
        onDrop: (nodeMoveEvent) => {
          dispatch(
            dropTransientNewFunnelInPlace({
              position: nodeMoveEvent.canvasGridPosition,
              sectionId: nodeMoveEvent.parentId,
            }),
          );
        },
      };
    }

    if (createTransientThirdPartyFunnel && createTransientThirdPartyFunnel.isPlacingFunnel) {
      return {
        nodeIds: [TRANSIENT_GROUP_ID],
        onDrop: async (nodePositionEvent) => {
          dispatch(
            dropTransientNewFunnelInPlace({
              position: nodePositionEvent.canvasGridPosition,
              sectionId: nodePositionEvent.parentId,
            }),
          );
          const newThirdPartyFunnel = await createThirdPartyFunnel({
            name: createTransientThirdPartyFunnel.name,
            system: createTransientThirdPartyFunnel.data.system,
            description: createTransientThirdPartyFunnel.data.description,
            steps: [],
            funnelLinks: [],
          });
          dispatch(removeAllTransientFunnelData());
          dispatch(
            addThirdPartyFunnelToCanvas({
              thirdPartyFunnel: newThirdPartyFunnel,
              position: nodePositionEvent.canvasGridPosition,
              sectionId: nodePositionEvent.parentId,
            }),
          );
          dispatch(addThirdPartyFunnel(newThirdPartyFunnel));
        },
      };
    }
  }, [
    readonly,
    placingTemplate,
    crmOrgId,
    importedSfFunnel,
    importThirdPartyFunnel,
    importedMetadataElements,
    createTransientSfFunnel,
    createTransientThirdPartyFunnel,
    fieldsPerTemplateMapping?.enabled,
    fieldsPerTemplateMapping?.sweepFieldsByApiName,
    dispatch,
    post_templateConvert,
    sendBiEvent,
    addNotification,
    createThirdPartyFunnel,
  ]);

  return {
    onSweepElementsChange: readonly ? noop : onSweepElementsChange,
    canvasNodesMouseMove: _moveGroups,
  };
};
